// Define the properties and their permissions for a resource
export interface ResourceProperties {
  [property: string]: PermissionsTypes[];
}

export interface FilterProperties {
  [property: string]: string;
}

export interface FunctionProperties {
  [property: string]: string;
}

// Define the structure for each resource
export interface Resource {
  resource: PermissionsTypes[]; // Permissions for the resource itself
  resource_function: string;
  filter_resource_function: string;
  properties: ResourceProperties; // Permissions for properties on this resource
  filter: FilterProperties; // Regex to filter items from the response
  properties_function: FunctionProperties; // Function to filter properties from the response
  // Merged permissions only (backend get_effective_privileges): EVERY role's
  // functions, sorted. Permissions are additive — each one is evaluated and
  // the highest result wins. The singular fields above are the first of
  // these (kept for older backends / role definitions).
  resource_functions?: string[];
  filter_resource_functions?: string[];
  properties_functions?: { [property: string]: string[] };
}

// Define global permissions for all resources
export interface ResourcePermissions {
  [resource: string]: Resource;
}

export interface GlobalPermissions {
  roles: string[];
  impersonated_roles: string[];
  // Cross-workspace user-impersonation (prod1 → prod). Set together when
  // the X-Impersonate-User header was honoured by the backend.
  impersonated_user?: string;
  impersonator_username?: string;
  impersonated_person_id?: string | null;
  permissions: ResourcePermissions;
  impersonation?: ResourcePermissions;
  subroles: Role[];
  role_data: Role[];
  resources: string[];
  permission_groups: PermissionGroup[];
}

// Add to your existing interfaces
export interface PermissionGroup {
  id: string;
  name: string;
  description?: string;
  permissions: ResourcePermissions;
}

export interface Role {
  selectedRole: any;
  id: string;
  parent_role: string | undefined;
  permissions: ResourcePermissions;
}

export enum PermissionsTypes {
  ReadWrite = 'ReadWrite',
  Read = 'Read',
  Function = 'Function',
  FilterFunction = 'FilterFunction',
  Deny = 'Deny',
  None = '',
}

// This is the order of permissions from least privileged to most privileged
export const PERMISSIONS_ORDER = [PermissionsTypes.Deny, PermissionsTypes.FilterFunction, PermissionsTypes.Function, PermissionsTypes.Read, PermissionsTypes.ReadWrite];

export interface PredefinedFunctions {
  name: string;
  type: PredefinedFunctionsTypes;
}

export enum PredefinedFunctionsTypes {
  Filter = 'filter_function',
  Checker = 'permission_checker',
  None = '',
}

export interface FilteredResourcePermission {
  id: string;
  permission: PermissionsTypes;
}

export interface FilteredResource {
  [resource: string]: FilteredResourcePermission[];
}
